import type { RootState } from '@/store/store';
import type { EChartsOption } from 'echarts';
import type { FC } from 'react';
import type { IChartLabels, ILineConfigItem } from '../../../types';

import { DashboardSearchContext } from '@/pages/dashboard-search';
import { init } from 'echarts';
import { useContext, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

type IBarStackChartProps = {
  height: number;
  data: ILineConfigItem[];
  showChart: boolean;
  labels: IChartLabels;
};

// type ISeriesItem = {
//   name: string;
//   type: string;
//   //   symbol: string;
//   stack: string;
//   data: number[];
//   emphasis: {
//     focus: string;
//   };
// };

type IDateSumsMap = { [key: string]: number };

const BarStackChart: FC<IBarStackChartProps> = ({
  data,
  height,
  showChart,
  labels,
}) => {
  const { currentTheme } = useSelector((state: RootState) => state.theme);
  const sharesRef = useRef<HTMLDivElement | null>(null);

  const { colorMap, onlyMyBrandsMap } = useContext(DashboardSearchContext);

  const options = useMemo(() => {
    const reversedData = [...data].reverse();

    const legend = [...new Set(reversedData.map(({ brand }) => brand))];
    const xAxisData = [...new Set(reversedData.map(({ date }) => date))];

    const sums: IDateSumsMap = xAxisData.reduce((acc, date) => {
      return { ...acc, [date]: 0 };
    }, {});

    reversedData.forEach(({ date, yValue }) => {
      if (sums?.[date] !== undefined) {
        sums[date] += yValue;
      }
    });

    return {
      tooltip: {
        trigger: 'axis',
        // valueFormatter: (value) => String((+value).toFixed(2)),
      },
      dataset: [
        {
          dimensions: ['brand', 'yValue', 'date'],
          source: reversedData.map(({ date, yValue, ...dataItem }) => ({
            ...dataItem,
            date,
            yValue: +((yValue / (sums[date] as number)) * 100).toFixed(1),
          })),
          id: 'dataset',
        },
        ...legend.map((brand) => ({
          fromId: 'dataset',
          id: brand,
          transform: {
            type: 'filter',
            config: { dimension: 'brand', value: brand },
          },
        })),
      ],
      series: legend.map((brand) => ({
        type: 'bar',
        name: brand,
        color: onlyMyBrandsMap?.[brand] ?? colorMap.get(brand),
        datasetId: brand,
        emphasis: {
          focus: 'series',
        },
        stack: 'date',
        encode: {
          x: 'date',
          y: 'yValue',
        },
      })),
      legend: {
        data: legend,
        type: 'scroll',
        bottom: 0,
        textStyle: {
          color: currentTheme === 'dark' ? 'white' : 'black',
        },
      },
      xAxis: {
        type: 'category',
        data: xAxisData,
        name: labels?.x,
        nameLocation: 'middle',
        nameGap: 25,
        axisLine: {
          onZero: false,
        },
      },
      yAxis: {
        type: 'value',
        name: labels?.y,
        max: 100,
        nameLocation: 'middle',
        nameRotate: 90,
        nameGap: 25,
      },
      dataZoom: [
        {
          type: 'inside',
        },
        {
          type: 'inside',
        },
      ],
    } as EChartsOption;
  }, [data, currentTheme, colorMap]);

  useEffect(() => {
    const myChart = init(sharesRef.current);
    showChart && myChart.setOption(options);

    return () => myChart.dispose();
  }, [options, height, showChart]);

  return (
    <div
      className={`${height === 500 ? 'h-[500px]' : 'h-[300px]'} w-full`}
      ref={sharesRef}
    />
  );
};

export default BarStackChart;
